import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useEffect } from 'react';
import { ensureNotificationPermission } from './notification-permission';
import { getFcmToken, registerTokenToBackend, subscribeForegroundMessages } from './notification-service';
import { useNotificationStore } from './notification-store';

/**
 * Registers FCM token to backend ONLY after login.
 * Also attaches a foreground message handler for in-app toasts later.
 */
export function useFcmRegistration() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const enabled = useNotificationStore((s) => s.enabled);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      if (!isAuthenticated || !enabled) return;

      const perm = await ensureNotificationPermission();
      if (perm !== 'granted') return;

      const token = await getFcmToken(import.meta.env.VITE_FCM_VAPID_KEY);
      if (!token) return;

      await registerTokenToBackend(token);

      unsubscribe =
        subscribeForegroundMessages(() => {
          // Play iPhone default notification sound
          const playNotificationSound = () => {
            const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
            const playTone = (frequency: number, duration: number, delay = 0, volume = 0.3) => {
              setTimeout(() => {
                const oscillator = audioContext.createOscillator();
                const gainNode = audioContext.createGain();

                oscillator.connect(gainNode);
                gainNode.connect(audioContext.destination);

                oscillator.frequency.value = frequency;
                oscillator.type = 'sine';

                gainNode.gain.setValueAtTime(0, audioContext.currentTime);
                gainNode.gain.linearRampToValueAtTime(volume, audioContext.currentTime + 0.01);
                gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + duration / 1000);

                oscillator.start(audioContext.currentTime);
                oscillator.stop(audioContext.currentTime + duration / 1000);
              }, delay);
            };

            // iPhone default notification sound pattern
            // First sequence: ascending tones
            playTone(800, 150, 0, 0.4);
            playTone(1000, 150, 100, 0.4);
            playTone(1200, 150, 200, 0.4);

            // Brief pause
            // Second sequence: descending tones
            playTone(1200, 120, 600, 0.3);
            playTone(1000, 120, 700, 0.3);
            playTone(800, 120, 800, 0.3);
          };

          playNotificationSound();
        }) ?? undefined;
    })();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [isAuthenticated, enabled]);
}
